/**
 * Project Context
 * 
 * Provides current project state, user role, and permissions throughout the app.
 * This is the foundation for the multi-project system.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from './AuthContext'
import { getProject, getProjectMember } from '../services/projects'
import { hasPermission } from '../config/permissions'

const ProjectContext = createContext(null)

export function ProjectProvider({ children, initialProjectId = null }) {
  const { user } = useAuth()
  
  // Project state
  const [projectId, setProjectId] = useState(initialProjectId)
  const [project, setProject] = useState(null)
  const [role, setRole] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Fetch project data and user's membership/role
   * Uses the projects service for Firestore operations
   */
  const fetchProject = useCallback(async (id) => {
    if (!id || !user) {
      setProject(null)
      setRole(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch project document
      const projectData = await getProject(id)
      
      if (!projectData) {
        setError('Project not found')
        setProject(null)
        setRole(null)
        return
      }

      // Fetch user's membership
      const membership = await getProjectMember(id, user.uid)
      
      setProject(projectData)
      setRole(membership?.role || null)
    } catch (err) {
      console.error('Error fetching project:', err)
      setError(err.message || 'Failed to load project')
      setProject(null)
      setRole(null)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Fetch project when projectId changes
  useEffect(() => {
    fetchProject(projectId)
  }, [projectId, fetchProject])

  // Clear project when user logs out
  useEffect(() => {
    if (!user) {
      setProject(null)
      setRole(null)
      setProjectId(null)
    }
  }, [user])

  /**
   * Permission check helper
   * Uses the centralized permissions config from src/config/permissions.js
   * 
   * @param {string} permission - The permission to check (create, edit, delete, manageMembers, deleteProject)
   * @returns {boolean} - Whether the user has the permission
   */
  const can = useCallback((permission) => {
    // If no project is selected, allow all (legacy single-project mode)
    // This ensures backward compatibility during migration
    if (!projectId || !project) {
      return true
    }

    // If user has no role in project, deny all except read
    if (!role) {
      return permission === 'read'
    }

    // Use the centralized hasPermission function from permissions config
    return hasPermission(role, permission)
  }, [projectId, project, role])

  /**
   * Refresh project data
   */
  const refreshProject = useCallback(async () => {
    if (projectId) {
      await fetchProject(projectId)
    }
  }, [projectId, fetchProject])

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Project data
    project,
    projectId,
    
    // User's role in current project
    role,
    
    // State flags
    loading,
    error,
    
    // Permission helper
    can,
    
    // Actions
    setProjectId,
    refreshProject,
  }), [project, projectId, role, loading, error, can, refreshProject])

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  )
}

/**
 * Hook to access project context
 * @returns {object} Project context value
 */
export function useProject() {
  const context = useContext(ProjectContext)
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider')
  }
  return context
}

/**
 * Convenience hook for permission checks
 * @returns {function} The can() permission check function
 */
export function useProjectPermissions() {
  const { can } = useProject()
  return can
}

export default ProjectContext
